<template>
  <!--
    A voice note, recorded where the work happens.

    It lives in the ui-kit and not in a module because the thing it produces is
    a platform thing: a File that `platform.files` stores. A crew reporting from
    a van, a driver explaining a delay, somebody dictating onto a customer's
    file — three modules, one microphone, one set of ways it can go wrong.

    The model value is a File or null. The parent uploads it whenever it uploads
    the rest of its form: nothing here talks to the network.
  -->
  <div class="rec">
    <!-- Recorded: what there is to keep, or throw away and do again. -->
    <template v-if="modelValue">
      <UiAudio :src="previewUrl" :label="label" removable @remove="discard" />
      <button type="button" class="rec__again" :disabled="disabled" @click="start">
        {{ uiLabel('audioRetry') }}
      </button>
    </template>

    <!-- Recording. -->
    <div v-else-if="recording" class="rec__live">
      <span class="rec__dot" aria-hidden="true" />
      <span class="rec__clock mono" role="timer">{{ formatClock(elapsed) }}</span>
      <span v-if="nearLimit" class="rec__limit">{{ formatClock(maxSeconds - elapsed) }}</span>
      <button type="button" class="rec__stop" @click="stop">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <rect x="5" y="5" width="14" height="14" rx="2" />
        </svg>
        {{ uiLabel('audioStop') }}
      </button>
    </div>

    <!-- Idle. -->
    <button
      v-else
      type="button"
      class="rec__start"
      :disabled="disabled || !supported"
      @click="start"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" />
      </svg>
      {{ uiLabel('audioRecord') }}
    </button>

    <p v-if="notice" class="rec__problem">{{ notice }}</p>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import UiAudio from './UiAudio.vue'
import { pickRecordingType, extensionFor, formatClock } from '../utils/audio'
import { uiLabel } from '../utils/labels'
import { useMediaCapture } from '../composables/useMediaCapture'

const props = defineProps({
  /** The recording, or null. A File, ready to go into a FormData. */
  modelValue: { type: Object, default: null },
  /** What a screen reader calls the clip once it exists. */
  label: { type: String, default: '' },
  /** Base name for the produced file; the extension follows the container. */
  filename: { type: String, default: 'voice-note' },
  /**
   * The ceiling, in seconds. Five minutes of opus is under a megabyte, so this
   * is not about the size limit — it is about the phone left in a pocket with
   * the recording running, which is how this feature actually gets abused.
   */
  maxSeconds: { type: Number, default: 300 },
  disabled: Boolean,
})

const emit = defineEmits(['update:modelValue', 'error'])

// The device, through the platform's one door: whether it can be asked for,
// what a refusal means, and giving the tracks back. Everything below this line
// is about MediaRecorder, which is this component's own business.
const microphone = useMediaCapture({ device: 'audio' })

const supported = microphone.available
  && typeof window !== 'undefined'
  && typeof window.MediaRecorder === 'function'

const recording = ref(false)
const elapsed = ref(0)
const problem = ref('')

const nearLimit = computed(() => props.maxSeconds - elapsed.value <= 30)

/**
 * Why the button is grey, said out loud and BEFORE anybody presses it.
 *
 * `problem` is only filled after a failed attempt — and a disabled button is
 * never attempted. So on a phone opening the ERP at `http://192.168.x.x`, which
 * is a crew's actual address, this was a grey button and not one word. Worse:
 * `localhost` IS a secure context, so it never happens to whoever built the
 * screen.
 */
const notice = computed(() => problem.value || (supported ? '' : microphone.problemLabel(microphone.blockedBy)))

// ── The object URL, and why it is watched rather than computed ───────────────
//
// createObjectURL hands out a reference the browser keeps alive until it is
// revoked. A computed would mint a new one on every re-render and leak the
// whole recording each time, which nobody notices until a long shift has filled
// the tab with them.
const previewUrl = ref('')

watch(() => props.modelValue, (file, previous) => {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
  previewUrl.value = file ? URL.createObjectURL(file) : ''
  if (file !== previous) elapsed.value = 0
}, { immediate: true })

let recorder = null
let stream = null
let ticker = null

function releaseMicrophone() {
  microphone.release(stream)
  stream = null
}

function stopTicking() {
  if (ticker) clearInterval(ticker)
  ticker = null
}

async function start() {
  if (props.disabled || !supported) return

  problem.value = ''
  emit('update:modelValue', null)

  try {
    stream = await microphone.capture({ audio: true })
  } catch (error) {
    // Refused, missing, in use by something else, or an insecure page. All four
    // are ordinary answers rather than faults: say WHICH one on the spot and
    // leave the button where it was.
    problem.value = microphone.problemLabel(error.reason)
    emit('error', error)
    return
  }

  const picked = pickRecordingType(window.MediaRecorder.isTypeSupported)
  const chunks = []

  try {
    recorder = picked
      ? new window.MediaRecorder(stream, { mimeType: picked.mimeType })
      : new window.MediaRecorder(stream)
  } catch (error) {
    releaseMicrophone()
    problem.value = uiLabel('audioUnsupported')
    emit('error', error)
    return
  }

  recorder.addEventListener('dataavailable', event => {
    if (event.data?.size) chunks.push(event.data)
  })

  recorder.addEventListener('stop', () => {
    stopTicking()
    releaseMicrophone()
    recording.value = false

    // The recorder's own type and not the one we asked for: where
    // isTypeSupported was missing the browser chose, and only it knows what.
    const type = recorder?.mimeType || picked?.mimeType || 'audio/webm'
    const blob = new Blob(chunks, { type })
    recorder = null

    if (!blob.size) {
      problem.value = uiLabel('audioUnsupported')
      return
    }

    emit('update:modelValue', new File(
      [blob], props.filename + '.' + extensionFor(type), { type: blob.type },
    ))
  })

  recorder.start()
  recording.value = true
  elapsed.value = 0

  ticker = setInterval(() => {
    elapsed.value += 1
    if (elapsed.value >= props.maxSeconds) stop()
  }, 1000)
}

function stop() {
  // `inactive` is what happens when the ceiling and a tap on Stop land in the
  // same tick; calling stop() again there throws.
  if (recorder && recorder.state !== 'inactive') recorder.stop()
  else { stopTicking(); releaseMicrophone(); recording.value = false }
}

function discard() {
  emit('update:modelValue', null)
  problem.value = ''
}

onBeforeUnmount(() => {
  // Navigating away mid-recording must not leave the microphone open.
  stop()
  stopTicking()
  releaseMicrophone()
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
})
</script>

<style scoped>
.rec { display: flex; flex-direction: column; gap: var(--space-2); }

.rec__start,
.rec__stop,
.rec__again {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border-radius: var(--radius);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: background var(--fast), border-color var(--fast), color var(--fast);
}

.rec__start {
  padding: var(--space-3) var(--space-4);
  border: 1px dashed var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-soft);
}
.rec__start:hover:not(:disabled) { border-color: var(--color-primary); color: var(--color-primary); }
.rec__start:disabled { cursor: not-allowed; opacity: 0.55; }

.rec__live {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-danger);
  border-radius: var(--radius);
  background: var(--color-danger-soft);
}

.rec__dot {
  width: 9px;
  height: 9px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--color-danger);
  animation: rec-pulse 1.2s ease-in-out infinite;
}
@keyframes rec-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
@media (prefers-reduced-motion: reduce) { .rec__dot { animation: none; } }

.rec__clock {
  flex: 1;
  font-size: var(--text-lg);
  color: var(--color-danger);
  font-variant-numeric: tabular-nums;
}

.rec__limit {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-danger);
}

.rec__stop {
  padding: var(--space-2) var(--space-3);
  border: none;
  background: var(--color-danger);
  color: #fff;
}

.rec__again {
  align-self: flex-start;
  padding: var(--space-1) var(--space-2);
  border: none;
  background: transparent;
  color: var(--color-primary);
}
.rec__again:hover:not(:disabled) { text-decoration: underline; }

.rec__problem {
  margin: 0;
  font-size: var(--text-xs);
  color: var(--color-danger);
}
</style>
